import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Search, Plus, Trophy, Target, TrendingUp, Medal, 
  Activity, Crown, Users2, Flame, Zap, Star, Shield, 
  MessageSquare, Sparkles
} from 'lucide-react';
import { ChatRoom } from '../components/club/ChatRoom';
import { clubs } from '../data/clubs';

const iconMap = {
  Zap,
  Crown,
  Flame,
  Shield
};

export function ClubPage() {
  const [showChat, setShowChat] = useState(false);

  return (
    <div className="space-y-6 pb-20">
      <div className="mt-16 sm:mt-0">
        <div className="flex items-center justify-between">
          <h1 className="text-xl sm:text-2xl font-bold">Club</h1>
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={() => setShowChat(!showChat)}
              className="theme-button px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg flex items-center gap-2 relative group overflow-hidden text-sm sm:text-base"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary via-purple-500 to-blue-500 opacity-0 group-hover:opacity-20 transition-opacity" />
              <MessageSquare className="w-4 h-4" />
              Club Chat
            </button>
            <button className="theme-button px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg flex items-center gap-2 relative group overflow-hidden text-sm sm:text-base">
              <div className="absolute inset-0 bg-gradient-to-r from-primary via-purple-500 to-blue-500 opacity-0 group-hover:opacity-20 transition-opacity" />
              <Plus className="w-4 h-4" />
              Create Club
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
          {/* Active Club Card */}
          <div className="theme-card p-4 sm:p-6 relative overflow-hidden backdrop-blur-xl border-primary/20">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
            <div className="relative space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Crown className="w-5 h-5 text-amber-500" />
                  <div>
                    <h2 className="text-lg font-semibold">Elite Performance Club</h2>
                    <p className="text-sm text-muted-foreground">28 active members</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-amber-500">Elite Status</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted/50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-4 h-4 text-primary" />
                    <span className="text-sm text-muted-foreground">Club Rank</span>
                  </div>
                  <div className="text-2xl font-bold">#3</div>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Medal className="w-4 h-4 text-primary" />
                    <span className="text-sm text-muted-foreground">Points</span>
                  </div>
                  <div className="text-2xl font-bold">2,450</div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Weekly Progress</span>
                  <span className="text-sm text-primary">75%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: '75%' }} />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex -space-x-2">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full bg-muted border-2 border-background"
                      style={{
                        backgroundImage: `url(https://i.pravatar.cc/32?img=${i + 1})`,
                        backgroundSize: 'cover'
                      }}
                    />
                  ))}
                  <div className="w-8 h-8 rounded-full bg-primary/20 border-2 border-background flex items-center justify-center">
                    <span className="text-xs text-primary">+24</span>
                  </div>
                </div>
                <button className="text-sm text-primary hover:text-primary/80 transition-colors">
                  View Members
                </button>
              </div>
            </div>
          </div>

          {/* Clubs Leaderboard */}
          <div className="theme-card p-4 sm:p-6 relative overflow-hidden backdrop-blur-xl border-primary/20">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
            <div className="relative">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Trophy className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold">Top Clubs</h2>
                </div>
                <button className="text-sm text-primary">View All</button>
              </div>

              <div className="space-y-4">
                {clubs.map((club) => {
                  const Icon = iconMap[club.icon as keyof typeof iconMap];
                  return (
                    <motion.div
                      key={club.id}
                      whileHover={{ scale: 1.02 }}
                      className="flex items-center justify-between p-4 bg-muted/50 rounded-lg cursor-pointer group"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          club.status === 'elite' ? 'bg-amber-500/20 text-amber-500' : 'bg-primary/20 text-primary'
                        }`}>
                          {Icon && <Icon className="w-4 h-4" />}
                        </div>
                        <div>
                          <div className="font-medium group-hover:text-primary transition-colors">{club.name}</div>
                          <div className="text-sm text-muted-foreground">{club.members} members</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{club.points.toLocaleString()}</div>
                        <div className="text-sm text-muted-foreground">points</div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Chat Section */}
        {showChat && (
          <div className="lg:sticky lg:top-6">
            <ChatRoom />
          </div>
        )}
      </div>
    </div>
  );
}